import { useMemo } from 'react'

import { useRouter } from 'next/router'

import getCurrentMonth from 'utils/get-current-month'
import { FeatureTabs } from 'utils/tabs'

import * as S from './MobileBottomNav.styles'

// Inline SVG icons — avoid depending on ClientBase Library icon set

const HomeIcon = ({ size = 22 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3 9.5 12 3l9 6.5V20a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V9.5Z" />
    <path d="M9 22V12h6v10" />
  </svg>
)

const UsersIcon = ({ size = 22 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
)

const ContractIcon = ({ size = 22 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M14 3v4a1 1 0 0 0 1 1h4" />
    <path d="M17 21H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7l5 5v11a2 2 0 0 1-2 2z" />
    <path d="M9 15c1.5 1.5 3.5 1 4-.5.5-1.5-1-2.5-2-2s-2.5.5-2 2c.5 1.5 2.5 2 4 .5" />
  </svg>
)

const ChargesIcon = ({ size = 22 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="1" y="4" width="22" height="16" rx="2" />
    <path d="M1 10h22" />
    <path d="M6 16h4" />
    <path d="M14 16h4" />
  </svg>
)

const RecurrenceIcon = ({ size = 22 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
    <path d="M21 3v5h-5" />
    <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
    <path d="M8 16H3v5" />
  </svg>
)

interface NavItemConfig {
  label: string
  icon: typeof UsersIcon
  pathname: string
  query: Record<string, string>
}

const navItems: NavItemConfig[] = [
  {
    label: 'Home',
    icon: HomeIcon,
    pathname: '/visao-geral',
    query: {}
  },
  {
    label: 'Clientes',
    icon: UsersIcon,
    pathname: '/clientes',
    query: { tab: FeatureTabs.CUSTOMERS }
  },
  {
    label: 'Contratos',
    icon: ContractIcon,
    pathname: '/comercial',
    query: { tab: FeatureTabs.CONTRACTS }
  },
  {
    label: 'Cobranças',
    icon: ChargesIcon,
    pathname: '/cobrancas',
    query: {
      tab: FeatureTabs.CHARGES,
      due_date_between: getCurrentMonth().join('|')
    }
  },
  {
    label: 'Recorrências',
    icon: RecurrenceIcon,
    pathname: '/cobrancas',
    query: { tab: FeatureTabs.RECURRENCES }
  }
]

const MobileBottomNav = () => {
  const router = useRouter()

  const activeIndex = useMemo(() => {
    const currentPath = router.pathname
    const currentTab = (router.query.tab as string) || ''

    for (let i = 0; i < navItems.length; i++) {
      const item = navItems[i]
      if (currentPath === item.pathname && currentTab === item.query.tab) {
        return i
      }
    }

    for (let i = 0; i < navItems.length; i++) {
      if (currentPath === navItems[i].pathname) {
        return i
      }
    }

    return -1
  }, [router.pathname, router.query.tab])

  const handleNavigate = (item: NavItemConfig) => {
    router.push({ pathname: item.pathname, query: item.query })
  }

  return (
    <S.NavBar>
      {navItems.map((item, index) => {
        const isActive = index === activeIndex
        const IconComponent = item.icon
        return (
          <S.NavItem
            key={item.label}
            isActive={isActive}
            onClick={() => handleNavigate(item)}
            type="button"
          >
            <IconComponent size={20} />
            <S.NavLabel isActive={isActive}>{item.label}</S.NavLabel>
          </S.NavItem>
        )
      })}
    </S.NavBar>
  )
}

export default MobileBottomNav
